import { IVocDetail } from "@/domain/entities/bbs";
import { IBookableDatesInfo } from "@/domain/entities/book";
import { IBusStopData } from "@/domain/entities/busStop";
import { IArrivalTimeOption, IBusLocation } from "@/domain/entities/lineInfo";

declare global {
  interface Window {
    kakao: any; // 카카오 맵
    naver: any; // 네이버 맵
    android: any;
    webkit: any;
    Kakao: any; // 카카오 로그인
    daum: any;
    MarkerClustering: any;
    eziok_std_process: any; // 본인인증
    fetchResult: any; // 본인인증 결과
    onKeyboardChanged: (show: boolean, keyboardHeight: number) => void;
    goToMap: (latitude: number, longitude: number) => void;
    setStatusBarHeight: (height: number) => void;
    setBottomNavBarHeight: (height: number) => void;
    setNotificationAllowStatus: (isEnabled: boolean) => void;
    setPushToken: (token: string) => void;
    setNotificationPermissionDetermined: (isDetermined: boolean) => void;
    onAppStatusChanged: (isActive: boolean) => void;
    hasNoCameraPermission: () => void;
    onCardScanned: (cardNumber?: number, expiryDate?: string) => void;
    askFinishApp: () => void;
    showLoadingView: (opacity: number) => void;
    removeLoadingView: () => void;
    paymentResult: (result: any) => void;
    openKCP: () => void;
    onBackPressed: () => void;
    openLinkWithExternalBrowser: (url: string) => void;
    onChangedWebviewSize: () => void;
    setCurrentLocation: (latitude: number, longitude: number) => void;
    hasNoLocationPermission: () => void;
  }
}

export const RouterName = {
  MainLogout: "MainLogout",
  Login: "Login",
  FindLoginInfo: "FindLoginInfo",
  TypesOfSignUp: "TypesOfSignUp",
  TermsOfUse: "TermsOfUse",
  /** 간편인증 */
  Auth: "Auth",
  AuthResult: "AuthResult",
  IdAuthentication: "IdAuthentication",
  SignUp: "SignUp",
  LineSearch: "LineSearch",
  LineInfoDetail: "LineInfoDetail",
  MapOfNearbyBusStops: "MapOfNearbyBusStops",
  MapOfLineBusStops: "MapOfLineBusStops",
  MainLogin: "MainLogin",
  BookFail: "BookFail",
  BookSuccess: "BookSuccess",
  BookConfirmation: "BookConfirmation",
  BookHistory: "BookHistory",
  BookBoardHistory: "BookBoardHistory",
  BoardHistory: "BoardHistory",
  BusStopInfoMap: "BusStopInfoMap",
  PaymentHistory: "PaymentHistory",
  ReportHistory: "ReportHistory",
  Contact: "Contact",
  MyPage: "MyPage",
  MyInfo: "MyInfo",
  MyInfoAuth: "MyInfoAuth",
  EditMyInfo: "EditMyInfo",
  ChangePassword: "ChangePassword",
  ChangeExpiredPassword: "ChangeExpiredPassword",
  ChangePhoneNumber: "ChangePhoneNumber",
  ChangeAddress: "ChangeAddress",
  WithDrawal: "WithDrawal",
  UsageGuide: "UsageGuide",
  TutorialGuide: "TutorialGuide",
  BusStopLayout: "BusStopLayout",
  Notice: "Notice",
  MobileId: "MobileId",
  /** 사내셔틀 */
  InsideShuttleMap: "InsideShuttleMap",
  TermsOutLink: "TermsOutLink",
  Chat: "Chat",
  NewRouteApplicationList: "NewRouteApplicationList",
  NewRouteApplicationDetail: "NewRouteApplicationDetail",
  NewRouteApplication: "NewRouteApplication",
  RouteDemandSurvey: "RouteDemandSurvey",
  BusCompanyList: "BusCompanyList",
  VocList: "VocList",
  VocDetail: "VocDetail",
  Voc: "Voc",
  LostFoundList: "LostFoundList",
  LostDetail: "LostDetail",
  LostCreate: "LostCreate",
  LostEdit: "LostEdit",
  FoundDetail: "FoundDetail",
  ServiceEvaluation: "ServiceEvaluation",
  BoardEvaluation: "BoardEvaluation",
  /** iOS 심사용 */
  ReviewMain: "ReviewMain",
  ReviewSearch: "ReviewSearch",
  ReviewLineInfoDetail: "ReviewLineInfoDetail",
  /** 구성원 인증 승인/반려용 */
  Cert: "Cert",
  AuthConfirm: "AuthConfirm",
  AuthReject: "AuthReject",
  /** 에러 페이지 */
  PageNotFound: "PageNotFound",
} as const;

export const RouterPath = {
  MainLogout: "/:serviceCode",
  Login: "/:serviceCode/login",
  FindLoginInfo: "/:serviceCode/login/find",
  TypesOfSignUp: "/:serviceCode/signup/types",
  TermsOfUse: "/:serviceCode/signup/terms",
  /** 간편인증 */
  Auth: "/:serviceCode/auth",
  AuthResult: "/:serviceCode/auth/result",
  IdAuthentication: "/:serviceCode/signup/auth/id",
  SignUp: "/:serviceCode/signup",
  LineSearch: "/:serviceCode/search",
  LineInfoDetail: "/:serviceCode/search/lineInfo/:lineId",
  MapOfNearbyBusStops: "/:serviceCode/nearby/busStops",
  MapOfLineBusStops: "/:serviceCode/line/:turnId/:busStopId",
  MainLogin: "/:serviceCode/main",
  BookFail: "/:serviceCode/book/fail",
  BookSuccess: "/:serviceCode/book/success",
  BookConfirmation: "/:serviceCode/book/confirmation",
  BookHistory: "/:serviceCode/history/book",
  BookBoardHistory: "/:serviceCode/history/book-board",
  BoardHistory: "/:serviceCode/history/board",
  PaymentHistory: "/:serviceCode/history/payment",
  ReportHistory: "/:serviceCode/history/report",
  BusStopInfoMap: "/:serviceCode/bus-stop/map/:busStopUid",
  Contact: "/:serviceCode/contact",
  MyPage: "/:serviceCode/mypage",
  MyInfo: "/:serviceCode/mypage/myinfo",
  MyInfoAuth: "/:serviceCode/mypage/auth",
  EditMyInfo: "/:serviceCode/mypage/myinfo/edit",
  ChangePassword: "/:serviceCode/mypage/myinfo/password",
  ChangeExpiredPassword: "/:serviceCode/mypage/myinfo/expired-password",
  ChangePhoneNumber: "/:serviceCode/mypage/myinfo/phone",
  ChangeAddress: "/:serviceCode/mypage/myinfo/address",
  WithDrawal: "/:serviceCode/mypage/myinfo/withdrawal",
  UsageGuide: "/:serviceCode/mypage/guide",
  TutorialGuide: "/:serviceCode/mypage/guide/tutorial",
  BusStopLayout: "/:serviceCode/mypage/platform-layout",
  Notice: "/:serviceCode/mypage/bbs",
  MobileId: "/:serviceCode/mypage/mobile-id",
  /** 사내셔틀 */
  InsideShuttleMap: "/:serviceCode/mypage/inside-shuttle",
  TermsOutLink: "/:serviceCode/terms/outlink",
  Chat: "/:serviceCode/chat",
  NewRouteApplicationList: "/:serviceCode/bbs/route/applications",
  NewRouteApplicationDetail: "/:serviceCode/bbs/route/applications/:uid",
  NewRouteApplication: "/:serviceCode/route/bbs/application/apply",
  RouteDemandSurvey: "/:serviceCode/route/demand/survey",
  BusCompanyList: "/:serviceCode/bus-company/list",
  VocList: "/:serviceCode/bbs/voc/list",
  VocDetail: "/:serviceCode/bbs/voc/list/:uid",
  Voc: "/:serviceCode/bbs/voc/write",
  LostFoundList: "/:serviceCode/bbs/lost-found/list",
  LostDetail: "/:serviceCode/bbs/lost-found/lost/:uid",
  LostCreate: "/:serviceCode/bbs/lost-found/lost/create",
  LostEdit: "/:serviceCode/bbs/lost-found/lost/:uid/edit",
  FoundDetail: "/:serviceCode/bbs/lost-found/found/:uid",
  ServiceEvaluation: "/:serviceCode/evaluation/service",
  BoardEvaluation: "/:serviceCode/survey/board",
  /** iOS 심사용 */
  ReviewMain: "/:serviceCode/review/main",
  ReviewSearch: "/:serviceCode/review/search",
  ReviewLineInfoDetail: "/:serviceCode/review/search/lineInfo/:lineId",
  /** 구성원 인증 승인/반려용 */
  Cert: "/:serviceCode/:token/cert",
  AuthConfirm: "/:serviceCode/auth/confirm",
  AuthReject: "/:serviceCode/auth/reject",
  /** 에러 페이지 */
  PageNotFound: "/not-found",
} as const;

export type RouterNameType = (typeof RouterName)[keyof typeof RouterName];

export enum LayoutMode {
  SIDE = "side",
  CENTER = "center",
  FULL = "full",
}

export const Position = {
  TOP: "top",
  BOTTOM: "bottom",
  LEFT: "left",
  RIGHT: "right",
} as const;

export type PositionType = (typeof Position)[keyof typeof Position];

export const SystemCodeType = {
  REPORT: "BBS-REPORT",
} as const;

export const CustomCodeGroup = {
  REPORT: "REP",
  /** 문의하기 */
  VOQ: "VOQ",
  /** 신고하기 */
  VOR: "VOR",
} as const;

export type CustomCodeGroupType =
  (typeof CustomCodeGroup)[keyof typeof CustomCodeGroup];

export const CustomCode = {
  REPORT: "REPORT",
  QNA: "QNA",
} as const;

export type CustomCodeType = (typeof CustomCode)[keyof typeof CustomCode];

export enum RankCode {
  /** 정규 */
  REG = "MUL-REG",
  /** 파트너 */
  BP = "MUL-BP",
  /** 기타 */
  NONE = "MUL-NONE",
}

export const RankCodeMap = {
  [RankCode.REG]: "정규직",
  [RankCode.BP]: "협력사",
  [RankCode.NONE]: "기타",
};

export enum DeviceType {
  Xsmall,
  Small,
  Medium,
  Large,
  Xlarge,
}

export enum LineTypeCode {
  Forward = "LRT-FORWAR",
  Backward = "LRT-BACKWA",
  Circular = "LRT-CIRCUL",
  Inside = "LRT-INSIDE",
  Shuttle = "LRT-SHUTTL",
}

export enum LineTypeName {
  Forward = "출근",
  Backward = "퇴근",
  Circular = "순환",
  Inside = "사내",
  Shuttle = "셔틀",
  None = "미지정",
}

export interface LineScheduleDisplay {
  days: { text: string; isToday: boolean }[];
  separator?: string;
}

export enum BookIntervalCode {
  DAY = "BDT-DAY",
  MONTH = "BDT-MONTH",
}

export enum BookIntervalStatus {
  BEGIN = "BEGIN",
  END = "END",
  CANCEL = "CANCEL",
}

export enum BookDateCode {
  FIXED = "BDC-FIXED", //고정일자
  DAYAFT = "BDC-DAYAFT", // 매월 특정일에 다음날 ~ 다음달 오픈일까지 예약오픈
  DAY = "BDC-DAY", // 매월 특정일에 다음달 예약오픈
  FIRSTDAY = "BDC-1STDAY", // 월첫운행일
  BOARD = "BDC-BOARD", // 탑승일기준
  BEGIN = "BDC-BEGIN", // 기점출발기준
  END = "BDC-END", // 종점도착기준
  WEEK = "BDC-WEEKDA", // 요일.매주O요일(오픈)"
  WINDOW = "BDC-WINDOW", // 고정일간격 0일단위
  NONE = "BDC-NONE", // 조건없음.종점도착일자(마감,취소)
}

export enum BookTimeCode {
  FIXED = "BTC-FIXED", //고정시각
  BEGIN = "BTC-BEGIN", // 기점출발
  END = "BTC-END", // 종점도착
  BOARD = "BTC-BOARD", // 승차장출발
  NONE = "BTC-NONE", // 없음
}

export enum BookStatusCode {
  END = "TXS-END", // 예약완료
  CANCEL = "TXS-CANCEL", // 예약취소
  ERROR = "TXS-ERROR", // 예약실패
  TEMP = "TXS-TEMP", // 임시저장
}

export enum BookStatusName {
  BOOK = "예약",
  CANCEL = "취소",
  BOARD = "탑승",
  NOSHOW = "미탑승",
}

export enum BookRequiredType {
  /** 모든 회차가 예약이 필요 */
  BOOK_REQUIRED = "BOOK_REQUIRED",
  /** 예약이 필요 없음 */
  NO_BOOK_REQUIRED = "NO_BOOK_REQUIRED",
  /** 예약 + 비예약이 섞여 있음 */
  MIXED = "MIXED",
}

export enum BookHistoryDateCriteria {
  BOOK = "book",
  BOARD = "board",
}

export type BookPeriod = {
  code: string; // 일예약 / 월예약
};

export enum BusStopEventCode {
  None = "None",
  FirstEnter = "FirstEnter",
  Enter = "Enter",
  Arrive = "Arrive",
  Leave = "Leave",
  LastLeave = "LastLeave",
}

export enum WeekdayCode {
  MON = "WKD-MON",
  TUE = "WKD-TUE",
  WED = "WKD-WED",
  THU = "WKD-THU",
  FRI = "WKD-FRI",
  SAT = "WKD-SAT",
  SUN = "WKD-SUN",
  HOLI = "WKD-HOLI",
  NONE = "",
}

export enum WeekdayCodeName {
  MON = "월",
  TUE = "화",
  WED = "수",
  THU = "목",
  FRI = "금",
  SAT = "토",
  SUN = "일",
  HOLI = "휴",
  NONE = "",
}

export interface ILineTypeCheckedMap {
  [lineTypeCode: string]: boolean;
}

export interface BusStopMarker {
  busStop: IBusStopData;
  marker: any;
  iconUrl?: any;
  title?: string;
}

export interface BusLocationMarker {
  busLocation: IBusLocation;
  marker: any;
}

export interface LatLng {
  latitude: number;
  longitude: number;
}

export type Notice = {
  id: string;
  category: string;
  date: string;
  title: string;
  content: string;
  wasRead: boolean;
  lastModifiedDate: string;
};

export type DeviceProps = {
  // statusBarHeightPx: string;
  // bottomNavHeightPx: string;
  isAndroid: boolean;
  isiOS: boolean;
};

export enum TermsTypeCode {
  USAGE = "AGC-USAGE",
  PERSON = "AGC-PERSON",
  PERSON_FULL = "AGC-PERFUL",
  MARKET = "AGC-MARKET",
  PERLOC = "AGC-PERLOC",
  LOCATION = "AGC-LOCATI",
}

export type CardType = {
  title: string;
  code: CardTypeCode;
};

export enum CardTypeCode {
  PERSONAL = "PERSONAL",
  CORPORATION = "CORPORATION",
}

export type PaymentMethod = {
  title: string;
  code: PaymentMethodCode;
};

export enum PaymentMethodCode {
  REGISTERED = "REGISTERED",
  NEW = "NEW",
}

export enum BookFailReason {
  DUPLICATED = "SEAT_DUPLICATED",
  FULL = "SEAT_FULL",
}

export type CardPaymentInfo = {
  paymentMethodCode: PaymentMethodCode;
  cardType: CardType;
  cardNumber: string;
  expiryDate: string;
  password: string;
  birthday?: string;
  businessNumber?: string;
  isSave: boolean;
};

export type CardPaymentInfoSchema = {
  cardNumber1?: string;
  cardNumber2?: string;
  cardNumber3?: string;
  cardNumber4?: string;
  expiryDate: string;
  password: string;
  birthday?: string;
  businessNumber?: string;
};

export type Address = {
  zipNo: string; // 우편번호
  dongNo: string; // 동번호
  dongAddress: string; // 지번주소
  roadNo: string; // 도로번호
  roadAddress: string; // 도로명주소
  detailAddress: string; // 상세주소
  lat: number; // 위도
  lng: number; // 경도
};

export type AddressData = {
  jibunAddress: string;
  autoJibunAddress: string;
  bcode: string;
  roadAddress: string;
  autoRoadAddress: string;
  roadnameCode: string;
  zonecode: string;
};

export enum AddressType {
  Home = "ADC-HOME",
  Office = "ADC-OFFICE",
}

export interface Menu {
  title: string;
  imageName: string;
  page: string;
}

export enum PGCode {
  KCP = "PGC-KCP",
  KakaoPay = "PGC-KAKAOP",
  FREE = "PGC-FREE",
}

export enum GuideTypeCode {
  GUIDE = "BBS-GUIDE",
  GUIDE_LOGOUT = "BBS-GUIDE-LOGOUT",
  OPERATION = "BBS-OPERA",
}

export interface CachedImage {
  url: string;
  timestamp: number;
}

export interface IBookableDatesResult {
  bookableDatesInfos: IBookableDatesInfo[];
  lineTurnUid: string;
}

export type KCPResult = {
  bookUid: string; // 202403081127212782081415586BOK
  resultCode: string;
  resultMessage: string;
  ordr_idxx: string; // 202403081127213421011154148KCP
};

export enum TimerStatus {
  /** 타이머 시작 */
  START = "START",
  /** 타이머 진행중 */
  PROGRESS = "PROGRESS",
  /** 타이머 중지 */
  STOP = "STOP",
}

export const BoardCheckCode = {
  EYECHK: "OCT-EYECHK", // 기사육안
  NONE: "OCT-NONE", // 안함
  QRTAG: "OCT-QRTAG", // QR태깅
  TKTCH: "OCT-TKTCH", // 모바일 티켓
  TFCARD: "OCT-TFCARD", // 교통카드 태깅
  TAGLES: "OCT-TAGLES", // 태그리스
} as const;

export const TalkType = {
  POST: "TKT-POST", // 게시물
  REPLY: "TKT-REPLY", // 답변
} as const;

export type TalkTypeCode = (typeof TalkType)[keyof typeof TalkType];

export const ReviewStatusCode = {
  /** 검토중 */
  REVIEW: "TLS-REVIEW",
  /** 검토승인 */
  ACCEPT: "TLS-ACCEPT",
  /** 처리불가 */
  REJECT: "TLS-REJECT",
  /** 처리완료 */
  COMPLETE: "TLS-COMPLE",
} as const;

export const BbsStatusCode = {
  /** 상태없음 */
  NONE: "TKS-NONE",
  /** 답변대기 */
  WAIT: "TKS-WAIT",
  /** 답변완료 */
  COMPLETE: "TKS-COMPLE",
} as const;

export const BbsType = {
  /** 이용제한문의 */
  NOSHOW: "BBS-NOSHOW",
  /** 공지사항 */
  NOTICE: "BBS-NOTICE",
  /** QnA */
  QNA: "BBS-QNA",
  /** 이용안내 */
  GUIDE: "BBS-GUIDE",
  /** FAQ */
  FAQ: "BBS-FAQ",
  /** 약관 */
  CLAUSE: "BBS-CLAUSE",
  /** 신고하기 */
  REPORT: "BBS-REPORT",
} as const;

export type BbsTypeCode = (typeof BbsType)[keyof typeof BbsType];

export const VocSubTypeCode = {
  /** 칭찬해요 */
  ADMIRE: "ADMIRE",
  /** 궁금해요 */
  WONDER: "WONDER",
  /** 불편해요 */
  UNREST: "UNREST",
  /** 건의해요 */
  ADVISE: "ADVISE",
  /** 찾아주세요 */
  LOST: "LOST",
} as const;

export const VocStatusCode = {
  /** 접수 */
  SUBMIT: "VOS-SUBMIT",
  /** 배분 */
  ASSIGN: "VOS-ASSIGN",
  /** 진행 */
  ACTIVE: "VOS-ACTIVE",
  /** 보류 */
  PAUSED: "VOS-PAUSED",
  /** 완료 */
  CLOSED: "VOS-CLOSED",
  /** 회신 */
  ANSWER: "VOS-ANSWER",
  /** 삭제 */
  DISCAR: "VOS-DISCAR",
} as const;

export type VocStatusCodeType =
  (typeof VocStatusCode)[keyof typeof VocStatusCode];

export type Location = {
  /** 위도 */
  latitude: number;
  /** 경도 */
  longitude: number;
};

export const Block = {
  BOOK: "bookBlock",
  BOARD: "boardBlock",
} as const;

export type BlockType = (typeof Block)[keyof typeof Block];

export const SignupFieldKey = {
  NAME: "JOIN.MEMBER_NAME",
  EMPLOYEE_NO: "JOIN.MEMBER_EMPLOYEE_NO",
  PHONE: "JOIN.MEMBER_PHONE",
  LOGIN_ID: "JOIN.LOGINID",
  NICKNAME: "JOIN.MEMBER_ALIAS",
} as const;

export type ResizeWebview = (
  withSafeArea: boolean,
  delayMillis?: number
) => void;

export type ShowErrorModal = (
  title?: string,
  message?: string,
  code?: number,
  time?: string,
  callback?: () => void
) => void;

export type ShowLoadingImage = () => void;

export type RemoveLoadingImage = () => void;

export type ShowToastMessage = (
  message: string,
  durationMillis?: number,
  delayMillis?: number
) => void;

export type GetCurrentLocation = () => void;

export type UpdateVocDetail = <K extends keyof IVocDetail>(
  key: K,
  value: IVocDetail[K]
) => void;

export type UpdateRequestGetOffBusStopId = (
  turnId: string,
  busStopId?: string
) => void;

export type GetMonthlyBookableDatesWithSeat = () => Promise<
  IBookableDatesInfo[]
>;
export type ShowMonthlyBookDetail = (datesInfos: IBookableDatesInfo[]) => void;
export type UpdateBookingDates = () => void;

export type HandleCheckPasswordExpired = () => void;

export type SetMyInfoFieldsToSessionStorage = () => void;

export type SetFormValuesToSessionStorage = () => void;

export let SERVICE_CODE = "SKHYNIX";
export function setSerivceCode(code: string): void {
  SERVICE_CODE = code;
}

export let SERVICE_UID = "202307031710447612665133592SVC";
export function setServiceUid(id: string): void {
  SERVICE_UID = id;
}
export const ACCESS_TOKEN = `${SERVICE_CODE}_ACCESS_TOKEN`;
export const REFRESH_TOKEN = `${SERVICE_CODE}_REFRESH_TOKEN`;
export const MEMBER_ID = `${SERVICE_CODE}_MEMBER_ID`;
export const AUTO_LOGIN = `${SERVICE_CODE}_AUTO_LOGIN`;
export const SERVICE_EVALUATION_POPUP = "SERVICE_EVALUATION_POPUP";
export const SERVICE_INFO = `${SERVICE_CODE}_SERVICE_INFO`;
export const HAS_READ_PERMISSIONS = `${SERVICE_CODE}_HAS_READ_PERMISSIONS`;
export const STORAGE_KEY_COMPLAINTS_SURVEY = `${SERVICE_CODE}_COMPLAINTS_SURVEY`;
export const STORAGE_KEY_PASSWORD_VALID_END_DATE = `${SERVICE_CODE}_PASSWORD_VALID_END_DATE`;
export const STORAGE_KEY_CHECK_PASSWORD_API_CALLED_DATE = `${SERVICE_CODE}_CHECK_PASSWORD_API_CALLED_DATE`;
export const SESSION_KEY_RANK_CODE = "SESSION_KEY_RANK_CODE";
export const SESSION_KEY_TERMS_RESULT = "SESSION_KEY_TERMS_RESULT";
export const SESSION_KEY_LOGIN_ID = "SESSION_KEY_LOGIN_ID";
export const SESSION_KEY_MASKING_USER_PHONE = "MASKING_USER_PHONE";
export const SESSION_KEY_EZIOK_CERT_AUTH_VALUE_ENC =
  "EZIOK_CERT_AUTH_VALUE_ENC";
export const SESSION_KEY_MYINFO_FIELDS = "MYINFO_FIELDS";
export const SESSION_KEY_FORM_VALUES = "FORM_VALUES";

export const VERIFICATION_TIMER = 180; // 초(seconds)
export const QRCODE_TIME_SECOND = 120; // 초(seconds)

export const PROVIDE_KEY_RESIZE_WEBVIEW = "resizeWebviewWithSafeArea";
export const PROVIDE_KEY_SHOW_ERROR_MODAL = "showErrorModal";
export const PROVIDE_KEY_SHOW_LOADING_IMAGE = "showLoadingImage";
export const PROVIDE_KEY_REMOVE_LOADING_IMAGE = "removeLoadingImage";
export const PROVIDE_KEY_SHOW_TOAST_MESSAGE = "showToastMessage";
export const PROVIDE_KEY_BUS_LOCATIONS = "busLocations";
export const PROVIDE_KEY_GET_CURRENT_LOCATION = "getCurrentLocation";
export const PROVIDE_KEY_CURRENT_LOCATION = "currentLocation";
export const PROVIDE_KEY_ARRIVAL_TIME_REMAINING = "arrivalTimeRemaining";
export const PROVIDE_KEY_UPDATE_VOC_DETAIL = "updateVocDetail";
export const PROVIDE_KEY_UPDATE_GET_OFF_BUS_STOP_ID =
  "updateRequestGetOffBusStopId";
export const PROVIDE_KEY_GET_MONTHLY_BOOKABLE_DATES_WITH_SEAT =
  "getMonthlyBookableDatesWithSeat";
export const PROVIDE_KEY_SHOW_MONTHLY_BOOK_DETAIL = "showMonthlyBookDetail";
export const PROVIDE_KEY_UPDATE_BOOKING_DATES = "updateBookingDates";
export const PROVIDE_KEY_HANDLE_CHECK_PASSWORD_EXPIRED =
  "handleCheckPasswordExpired";
export const PROVIDE_KEY_IS_BUS_RUNNING = "isBusRunning";
export const PROVIDE_KEY_TURN_BUSES_REMAIN_SEAT_COUNT =
  "turnBusLastSeatCountList";
export const PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED =
  "isLocationPermissionDenied";
export const PROVIDE_KEY_SET_MYINFO_FIELDS = "setMyInfoFieldsToSessionStorage";
export const PROVIDE_KEY_SET_FORM_VALUES = "setFormValuesToSessionStorage";
export const PROVIDE_KEY_NEXT_BUS_STOP_CHANGE_DATE_TIME =
  "nextBusStopChangeDateTime";

export const NUMBER_OF_BOOKINFO_PER_PAGE = 5;
export const BUS_LOCATION_CACHED_TIME_MILLIS = 3500;
export const BUS_LOCATION_REFRESH_TIME_MILLIS = 10000;
export const ON_BOARD_LINE_CALLING_TIME_MILLIS = 30000;
export const CACHED_BUS_IMAGE_KEY_PREFIX = "busImage_";
export const CACHED_QNA_IMAGE_KEY_PREFIX = "qnaImage_";
export const USE_RESTRICTED_CHECK_TIME_MILLIS = 1000;
export const ERROR_CODE_BOOK_BLOCKED = 403000101;
export const LAST_STOP_ARRIVAL_TIME_THRESHOLD_MIN = 180;
export const BUS_STOP_MARKERS_WINDOW_SIZE_METER = 20000;

// swiper
export type Effect = "slide" | "fade" | "cube" | "coverflow" | "flip";
export type PaginationType = "bullets" | "fraction" | "progressbar" | "custom";

// input
export type InputMode =
  | "text"
  | "search"
  | "none"
  | "tel"
  | "url"
  | "email"
  | "numeric"
  | "decimal"
  | undefined;

export interface IOption {
  name: string;
  value: string;
  data?: any;
}

export const ErrorTypeCode = {
  MONTHLY_BOOK_UNAVAILABLE: "MONTHLY_BOOK_UNAVAILABLE",
  EMPTY_MONTHLY_BOOKABLE_DATES: "EMPTY_MONTHLY_BOOKABLE_DATES",
  NOT_FOUND_ACCESS_TOKEN: "NOT_FOUND_ACCESS_TOKEN",
};

export type ErrorType = (typeof ErrorTypeCode)[keyof typeof ErrorTypeCode];

export const ArrivalTimeOptionCode = {
  /** 목표도착시각 */
  STANDARD: "STANDARD",
  /** 평균도착시각 */
  AVG: "AVERAGE",
  /** Tmap예상도착시각 */
  TMAP: "TMAP",
  /** 과거도착시각 */
  PAST: "PAST",
} as const;

export type ArrivalTimeOptionCodeType =
  (typeof ArrivalTimeOptionCode)[keyof typeof ArrivalTimeOptionCode];

export const arrivalTimeOptions: IArrivalTimeOption[] = [
  { name: "목표도착시각", type: ArrivalTimeOptionCode.STANDARD },
  { name: "평균도착시각", type: ArrivalTimeOptionCode.AVG },
  { name: "Tmap예상도착시각", type: ArrivalTimeOptionCode.TMAP },
  { name: "과거도착시각", type: ArrivalTimeOptionCode.PAST },
];

export const AllocStatusCode = {
  /** 운행정보 - 배차확정 */
  CONFIRM: "AIS-CONFIR",
  /** 운행준비 - 점호시작 */
  ROLL_CALL: "AIS-ROLCAL",
  /** 운행중 */
  DRIVING: "AIS-DRVING",
  /** 운행 종료 */
  DRIVEN: "AIS-DRVEND",
  /** 미배차 */
  NOT_YET: "AIS-NOTYET",
} as const;

export type AllocStatusCodeType =
  (typeof AllocStatusCode)[keyof typeof AllocStatusCode];

/** 만료된 비밀번호 변경 페이지로 이동하게 된 이유 */
export const ChangePasswordExpiredSourceType = {
  LOGIN: "login",
  ACTIVE: "active-status",
} as const;

export const FILTER_KEY_PERIOD = "조회기간";
export const FILTER_KEY_TYPE = "필터";
export const FILTER_KEY_SORT = "정렬기준";

export const BookStatus = {
  Book: "Book",
  PastBook: "PastBook",
  All: "All",
} as const;
export type BookStatusType = (typeof BookStatus)[keyof typeof BookStatus];

export const CalendarDisplayMode = {
  Chip: "chip",
  Bar: "bar",
} as const;

export type CalendarDisplayModeType =
  (typeof CalendarDisplayMode)[keyof typeof CalendarDisplayMode];

export interface CalendarEventInfo<T> {
  /** 날짜: YYYY-MM-DD 형식 "2024-04-16" */
  date: string;
  eventTitles: string[];
  detailEvents: T[];
}

export interface ITurnBusLastSeatCount {
  allocUid: string;
  remainSeatCount: number;
  boardingCount: number;
}

export const SurveyTypeCode = {
  LINE: "SVT-LINE",
  BOARD: "SVT-BOARD",
} as const;

export type SurveyTypeCodeType =
  (typeof SurveyTypeCode)[keyof typeof SurveyTypeCode];

export const NaveMapPolyLineStyle = {
  SOLID: "solid",
  SHORT_DASH: "shortdash",
  SHORT_DOT: "shortdot",
} as const;

export type NaverMapPolyLineStyleType =
  (typeof NaveMapPolyLineStyle)[keyof typeof NaveMapPolyLineStyle];

export const FavoriteLinesViewType = {
  LIST: "list",
  CARD: "card",
} as const;

export type TypeofFavoriteLinesViewType =
  (typeof FavoriteLinesViewType)[keyof typeof FavoriteLinesViewType];

export const LostFoundTypeCode = {
  Lost: "lost",
  Found: "found",
} as const;

export type LostFoundType =
  (typeof LostFoundTypeCode)[keyof typeof LostFoundTypeCode];

export const LostFoundStatusCode = {
  /** 반환  */
  RECOVE: "LST-RECOVE",
  /** 접수  */
  STORE: "LST-STORE",
  /** 폐기  */
  DISCAR: "LST-DISCAR",
} as const;

export type AddressItem = {
  addrdetail: {
    country: string;
    dongmyun: string;
    rest: string;
    ri: string;
    sido: string;
    sigugun: string;
  };
  address: string;
  isRoadAddress: boolean;
};

export type GeocodeResponse = {
  result: {
    items: AddressItem[];
    userquery: string;
  };
  v2: {
    address: {
      roadAddress: string;
      jibunAddress: string;
    };
    results: {
      name: string;
      land: {
        addition0: {
          type: string;
          value: string;
        };
        addition1: {
          type: string;
          value: string;
        };
        number1: number;
        number2: number;
      };
    }[];
  };
};

export type HynixCoordinate = {
  campusName: string;
  lat: number;
  lng: number;
  radiusMeter: number;
};

export const CampaignImageTypeCode = {
  BANNER: "CIT-BANNER",
  CARD_NEWS: "CIT-CARDNE",
} as const;

export type CampaignImageType =
  (typeof CampaignImageTypeCode)[keyof typeof CampaignImageTypeCode];

export const MessageTypeCode = {
  FAVORITE: "favorite",
  GET_ON_NOTIFICATION: "getOnNotification",
  GET_OFF_NOTIFICATION: "getOffNotification",
} as const;

export type MessageTypeCodeType =
  (typeof MessageTypeCode)[keyof typeof MessageTypeCode];

export const ACTION_MESSAGES = {
  [MessageTypeCode.FAVORITE]: {
    success: {
      add: "즐겨찾기가 추가되었습니다",
      delete: "즐겨찾기가 삭제되었습니다",
    },
    failure: {
      add: "즐겨찾기 추가에 실패했습니다",
      delete: "즐겨찾기 삭제에 실패했습니다",
    },
  },
  [MessageTypeCode.GET_ON_NOTIFICATION]: {
    success: {
      add: "승차 버스 도착 사전 알림이 설정되었습니다",
      delete: "승차 버스 도착 사전 알림이 해제되었습니다",
    },
    failure: {
      add: "승차 버스 도착 사전 알림 설정에 실패했습니다",
      delete: "승차 버스 도착 사전 알림 해제에 실패했습니다",
    },
  },
  [MessageTypeCode.GET_OFF_NOTIFICATION]: {
    success: {
      add: "하차 버스 도착 사전 알림이 설정되었습니다",
      delete: "하차 버스 도착 사전 알림이 해제되었습니다",
    },
    failure: {
      add: "하차 버스 도착 사전 알림 설정에 실패했습니다",
      delete: "하차 버스 도착 사전 알림 해제에 실패했습니다",
    },
  },
};
